import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LoginMethod } from '@linktivity/ars-neo';
import {
  type FormValues,
  type Rules,
  Button,
  Form,
  Input
} from '@linktivity/link-ui';
import { DEFAULT_LOGIN_METHODS, EMAIL_REGEX } from '@link/share/constants';
import Apple from '@link/share/icons/apple.svg?react';
import Google from '@link/share/icons/google.svg?react';
import Mail from '@link/share/icons/mail.svg?react';
import { BaseAuth } from '@link/share/stores';
import ShareAuth from '@link/share/vendors/firebase';
import styles from './login.module.css';

interface LoginMethodsProps {
  onSubmit: (email: string, step: string) => void;
  firebase: ShareAuth;
  auth: BaseAuth;
  loginMethods?: LoginMethod[];
}

const LoginMethods = ({
  onSubmit,
  firebase,
  auth,
  loginMethods
}: LoginMethodsProps) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<FormValues>({
    email: '',
    password: ''
  });

  const [valid, setValid] = useState(true);
  const [emailMode, setEmailMode] = useState(false);

  const methods = useMemo(() => {
    if (loginMethods && loginMethods.length > 0) {
      return loginMethods;
    }
    return DEFAULT_LOGIN_METHODS;
  }, [loginMethods]);

  const rules: Rules = useMemo(
    () => ({
      email: [
        {
          required: true,
          message: t('share.validation.required', {
            field: t('share.login.email')
          }),
          trigger: ['change', 'blur']
        },
        {
          pattern: EMAIL_REGEX,
          message: t('share.validation.invalid', {
            field: t('share.login.email')
          }),
          trigger: 'blur'
        }
      ]
    }),
    [t]
  );

  const googleLogin = useCallback(() => {
    firebase.googleLogin().then(credential => {
      if (credential) {
        auth.updateToken(credential);
      }
    });
  }, [auth, firebase]);

  const appleLogin = useCallback(() => {
    firebase.appleLogin().then(credential => {
      if (credential) {
        auth.updateToken(credential);
      }
    });
  }, [auth, firebase]);

  const emailLogin = useCallback(() => {
    setEmailMode(true);
  }, []);

  const handleSubmit = useCallback(() => {
    firebase.checkUserExists(formValues.email).then(user => {
      if (user) {
        onSubmit(formValues.email, 'signin');
      } else {
        onSubmit(formValues.email, 'signup');
      }
    });
  }, [firebase, formValues.email, onSubmit]);

  return (
    <div className={styles.content}>
      {methods.includes(LoginMethod.LoginMethodGoogle) && (
        <Button
          className={styles.button}
          onClick={googleLogin}
          variant="outlined"
        >
          <Google className={styles.buttonIcon} />
          <span className={styles.buttonText}>
            {t('share.login.googleLogin')}
          </span>
        </Button>
      )}
      {methods.includes(LoginMethod.LoginMethodApple) && (
        <Button
          className={styles.button}
          onClick={appleLogin}
          variant="outlined"
        >
          <Apple className={styles.buttonIcon} />
          <span className={styles.buttonText}>
            {t('share.login.appleLogin')}
          </span>
        </Button>
      )}
      {methods.includes(LoginMethod.LoginMethodEmail) && (
        <Button
          className={styles.button}
          onClick={emailLogin}
          variant="outlined"
        >
          <Mail className={styles.buttonIcon} />
          <span className={styles.buttonText}>{t('share.login.email')}</span>
        </Button>
      )}
      {methods.includes(LoginMethod.LoginMethodEmail) && emailMode && (
        <Form
          className={styles.form}
          values={formValues}
          onValuesChange={setFormValues}
          onValidateChange={setValid}
          onSubmit={handleSubmit}
          rules={rules}
        >
          <Form.Item
            label={t('share.login.email')}
            field="email"
            htmlFor="email"
          >
            <Input id="email" />
          </Form.Item>
          <Form.Item>
            <Button type="submit" disabled={!valid}>
              {t('share.common.next')}
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  );
};

export default LoginMethods;
